<template>
    <div class="deal-picker">
        <div class="deal-picker-header">
            <div class="deal-picker-input">
                <input 
                    v-model.number="localDealNumber" 
                    placeholder="Deal #" 
                    class="deal-picker-input-field"
                    :disabled="disabled"
                    @input="onDealNumberInput"
                />
            </div>
            <div class="deal-picker-pagination">
                <a @click="!disabled && moreDeals(-1)" :class="{ 'deal-picker-link-disabled': disabled }">PREV</a>
                <a @click="!disabled && moreDeals(1)" :class="{ 'deal-picker-link-disabled': disabled }">NEXT</a>
                <a @click="!disabled && reset()" :class="{ 'deal-picker-link-disabled': disabled }">RESET</a>
            </div>
        </div>
        <div v-if="latestDeals && latestDeals.length > 0" class="deal-picker-grid">
            <div 
                v-for="(deal, idx) in latestDeals" 
                :key="deal.dealId || idx"
                class="deal-picker-item"
                :class="{ 'deal-picker-item-loading': isSelectingDeal && localDealNumber === deal.dealId, 'deal-picker-item-disabled': isSelectingDeal || disabled }"
                @click="deal.dealId && !isSelectingDeal && !disabled ? selectDeal(deal.dealId) : null"
            >
                <span class="deal-picker-number">{{((pageNumber - 1) * 10) + idx + 1}}</span>
                <span class="deal-picker-id">{{deal.dealId || 'N/A'}}</span>
                <img 
                    v-if="deal.imgUrl" 
                    :src="deal.imgUrl" 
                    class="deal-picker-thumbnail"
                    @click.stop="deal.dealId && !isSelectingDeal && !disabled ? selectDeal(deal.dealId) : null"
                />
                <div v-if="isSelectingDeal && localDealNumber === deal.dealId" class="deal-picker-item-loading-overlay">
                    <img :src="`/Assets/img/spin.svg`" class="deal-picker-item-spinner" />
                </div>
            </div>
        </div>
        <div v-else-if="latestDeals && latestDeals.length === 0" class="deal-picker-empty">
            No deals found
        </div>
        <div v-show="isLoading" class="deal-picker-loading">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> LOADING
        </div>
        <div v-show="isSelectingDeal" class="deal-picker-loading">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> Loading deal...
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue'
import api from '@/api'
import { DealDetails } from '@/classes/DealDetails'

export default defineComponent({
    name: 'DealPicker',
    props: {
        modelValue: {
            type: Number,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'dealSelected'],
    setup(props, { emit }) {
        const latestDeals = ref<DealDetails[] | null>(null)
        const pageNumber = ref(1)
        const isLoading = ref(false)
        const isSelectingDeal = ref(false)
        const localDealNumber = ref<number | null>(props.modelValue)

        watch(() => props.modelValue, (newValue) => {
            localDealNumber.value = newValue
        })

        const getLatestDeals = async () => {
            isLoading.value = true
            try {
                const response = await api.getLatestDeals(10, pageNumber.value, null)
                // Convert plain objects to DealDetails instances to ensure proper property mapping
                latestDeals.value = response.data.map((deal: any) => new DealDetails(deal))
            } catch (error) {
                console.error('Failed to load latest deals:', error)
            } finally {
                isLoading.value = false
            }
        }

        const moreDeals = (amt: number) => {
            if (props.disabled) {
                return
            }
            pageNumber.value = Math.max(1, pageNumber.value + amt)
            getLatestDeals()
        }

        const reset = () => {
            if (props.disabled) {
                return
            }
            pageNumber.value = 1
            getLatestDeals()
        }

        const selectDeal = async (dealId: number) => {
            if (props.disabled) {
                return
            }
            if (!dealId) {
                console.error('No dealId provided to selectDeal')
                return
            }
            isSelectingDeal.value = true
            localDealNumber.value = dealId
            emit('update:modelValue', dealId)
            
            try {
                const response = await api.getDeal(dealId)
                if (response && response.data) {
                    // Convert to DealDetails instance to ensure proper property mapping
                    const deal = new DealDetails(response.data)
                    emit('dealSelected', deal)
                } else {
                    console.error('No deal data returned from API for dealId:', dealId)
                }
            } catch (error) {
                console.error('Failed to fetch deal details:', error)
                // Still emit the dealId even if fetch fails
                emit('update:modelValue', dealId)
            } finally {
                isSelectingDeal.value = false
            }
        }

        const onDealNumberInput = () => {
            if (localDealNumber.value) {
                emit('update:modelValue', localDealNumber.value)
                selectDeal(localDealNumber.value)
            }
        }

        onMounted(() => {
            getLatestDeals()
            if (props.modelValue) {
                selectDeal(props.modelValue)
            }
        })

        return {
            latestDeals,
            pageNumber,
            isLoading,
            isSelectingDeal,
            localDealNumber,
            moreDeals,
            reset,
            selectDeal,
            onDealNumberInput
        }
    }
})
</script>

<style scoped>
.deal-picker {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.deal-picker-header {
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
}

.deal-picker-input {
    flex: 1;
}

.deal-picker-input-field {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
}

.deal-picker-input-field:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.deal-picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
}

.deal-picker-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;
}

.deal-picker-item:hover:not(.deal-picker-item-disabled) {
    border-color: #2196F3;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.deal-picker-item-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.deal-picker-item-loading {
    position: relative;
    border-color: #2196F3;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.deal-picker-item-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
}

.deal-picker-item-spinner {
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
}

.deal-picker-number {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.deal-picker-id {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
}

.deal-picker-thumbnail {
    max-height: 50px;
    max-width: 100%;
    object-fit: contain;
    border-radius: 4px;
}

.deal-picker-pagination {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-shrink: 0;
}

.deal-picker-pagination a {
    cursor: pointer;
    color: #2196F3;
    text-decoration: none;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

.deal-picker-pagination a:hover:not(.deal-picker-link-disabled) {
    background-color: #e3f2fd;
}

.deal-picker-link-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.deal-picker-loading {
    text-align: center;
    padding: 20px;
    color: #666;
}

.deal-picker-loading .icon {
    display: inline-block;
    margin-right: 10px;
}

.deal-picker-loading .icon img {
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>

